(() => {
  if (window.BhdInboxChatLinksInitialized) return;
  window.BhdInboxChatLinksInitialized = true;

  const TRIGGER_SELECTOR = '[data-inbox-chat-trigger], a[href="#chat"]';
  const CHAT_HOST_SELECTOR = [
    'inbox-online-store-chat',
    'shopify-chat',
    '#ShopifyChat',
    '[id*="ShopifyChat"]',
    '[id*="shopify-chat"]'
  ].join(',');
  const LAUNCHER_SELECTORS = [
    'button.chat-toggle',
    'button.chat-toggle--bottom-right',
    'button.chat-toggle--bottom-left',
    '.chat-app > button',
    'button.activator',
    '.activator',
    'button[part*="activator"]',
    'button[aria-label*="chat" i]',
    'button[data-testid*="chat" i]'
  ];

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const isCloseControl = (element) => {
    if (!element) return true;
    if (element.matches?.('.chat-app--close-button, [data-testid*="close" i]')) return true;

    const label = [
      element.getAttribute?.('aria-label'),
      element.getAttribute?.('title'),
      element.textContent
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return /close|minimi[sz]e/.test(label);
  };

  const getChatRoots = () => {
    const roots = [document];
    const seenRoots = new Set(roots);
    const hosts = Array.from(document.querySelectorAll(CHAT_HOST_SELECTOR));
    const seenHosts = new Set(hosts);

    for (let hostIndex = 0; hostIndex < hosts.length; hostIndex += 1) {
      const host = hosts[hostIndex];
      if (host.shadowRoot && !seenRoots.has(host.shadowRoot)) {
        seenRoots.add(host.shadowRoot);
        roots.push(host.shadowRoot);

        // Shopify Inbox can nest its current <shopify-chat> component inside
        // another shadow root. Walk only the chat subtree, not the whole page.
        host.shadowRoot.querySelectorAll('*').forEach((element) => {
          if (element.shadowRoot && !seenRoots.has(element.shadowRoot)) {
            seenRoots.add(element.shadowRoot);
            roots.push(element.shadowRoot);
          }
          if (element.matches?.(CHAT_HOST_SELECTOR) && !seenHosts.has(element)) {
            seenHosts.add(element);
            hosts.push(element);
          }
        });
      }
    }

    return roots;
  };

  const findLauncherButtons = () => {
    const buttons = [];
    const seen = new Set();

    getChatRoots().forEach((root) => {
      LAUNCHER_SELECTORS.forEach((selector) => {
        root.querySelectorAll?.(selector).forEach((element) => {
          if (!(element instanceof HTMLElement) || seen.has(element) || isCloseControl(element)) return;
          seen.add(element);
          buttons.push(element);
        });
      });
    });

    return buttons;
  };

  const isChatOpen = () => {
    const legacyHost = document.querySelector('inbox-online-store-chat#ShopifyChat, inbox-online-store-chat');
    if (legacyHost?.getAttribute?.('is-open') === 'true') return true;

    return findLauncherButtons().some((button) => {
      return button.getAttribute?.('aria-expanded') === 'true' || button.getAttribute?.('data-state') === 'open';
    });
  };

  const hideNativeLauncher = () => {
    findLauncherButtons().forEach((button) => {
      button.style.setProperty('opacity', '0', 'important');
      button.style.setProperty('visibility', 'hidden', 'important');
      button.style.setProperty('pointer-events', 'none', 'important');
    });
  };

  const openInbox = async () => {
    if (isChatOpen()) return true;

    for (let attempt = 0; attempt < 70; attempt += 1) {
      const button = findLauncherButtons()[0];
      if (button) {
        button.click();
        await sleep(160);

        // Legacy Inbox exposes is-open; the newer component does not always
        // expose state, so a successful programmatic click is enough to stop retrying.
        hideNativeLauncher();
        return true;
      }
      await sleep(100);
    }

    return false;
  };

  const closeMobileDrawer = (trigger) => {
    const drawer = trigger.closest('header-drawer');
    const details = drawer?.querySelector('details.menu-drawer-container[open]');
    if (!details) return;

    const summary = details.querySelector(':scope > summary');
    if (summary) {
      summary.click();
    } else {
      details.removeAttribute('open');
    }
  };

  const ensureFooterChatLink = () => {
    document.querySelectorAll('footer-accordion.footer-block--menu').forEach((block) => {
      const heading = block.querySelector('.accordion__title')?.textContent?.trim().toUpperCase();
      if (heading !== 'CUSTOMER SERVICE') return;

      const list = block.querySelector('ul.footer-block__details-content');
      if (!list || list.querySelector('[data-inbox-chat-trigger]')) return;

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = '#chat';
      link.className = 'link list-menu__item list-menu__item--link';
      link.setAttribute('data-inbox-chat-trigger', '');
      link.textContent = document.documentElement.lang?.toLowerCase().startsWith('es')
        ? 'Chatea con nosotros'
        : 'Chat With Us';

      item.appendChild(link);
      list.appendChild(item);
    });
  };

  const maintainInboxUi = () => {
    ensureFooterChatLink();
    hideNativeLauncher();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(TRIGGER_SELECTOR);
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    closeMobileDrawer(trigger);

    window.setTimeout(async () => {
      const opened = await openInbox();
      if (!opened) console.warn('Shopify Inbox did not become available in time.');
    }, 220);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maintainInboxUi, { once: true });
  } else {
    maintainInboxUi();
  }

  // Inbox can rebuild its shadow DOM after opening/closing, so periodically
  // re-hide only the native launcher while leaving the conversation panel usable.
  window.setInterval(maintainInboxUi, 1000);
})();

/* BHD v20: freeze the storefront behind mobile drawers and make the exposed edge a dismiss target. */
(() => {
  if (window.BhdGlobalDrawerLockInitialized) return;
  window.BhdGlobalDrawerLockInitialized = true;

  const mobile = window.matchMedia('(max-width: 989px)');
  const LOCK_CLASSES = [
    'menu-mobile--opening',
    'menu-mobile--open',
    'menu-mobile--closing',
    'facet-drawer--opening',
    'facet-drawer--open',
    'facet-drawer--closing'
  ];

  let locked = false;
  let lockedScrollY = 0;
  let previousBodyStyles = null;
  let unlockTimer = 0;

  const style = document.createElement('style');
  style.id = 'bhd-global-drawer-lock-style';
  style.textContent = `
    @media (max-width: 989px) {
      html.bhd-drawer-page-locked,
      body.bhd-drawer-page-locked {
        overflow: hidden !important;
        overscroll-behavior: none !important;
      }

      body.bhd-drawer-page-locked {
        position: fixed !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
      }

      body.menu-mobile--opening .menu-drawer-container > summary.header__icon--menu::after,
      body.menu-mobile--open .menu-drawer-container > summary.header__icon--menu::after,
      body.menu-mobile--closing .menu-drawer-container > summary.header__icon--menu::after {
        pointer-events: auto !important;
        touch-action: none !important;
        overscroll-behavior: none !important;
      }

      .menu-drawer__navigation-container,
      .bhd-mini-cart-scroll,
      .mobile-facets__inner {
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
      }
    }
  `;
  document.head.appendChild(style);

  const bodyHasAny = (classes) => classes.some((className) => document.body.classList.contains(className));

  const getOpenDrawer = () => {
    const hosts = Array.from(document.querySelectorAll('header-drawer, cart-drawer, facet-drawer'));

    return hosts.find((host) => {
      const openDetails = host.querySelector(
        ':scope > details[open], details.menu-drawer-container[open], details.cart-drawer-container[open]'
      );
      if (openDetails) return true;

      if (host.matches('header-drawer')) {
        return bodyHasAny(['menu-mobile--opening', 'menu-mobile--open', 'menu-mobile--closing']);
      }

      if (host.matches('cart-drawer')) {
        return bodyHasAny(['mini-cart--opening', 'mini-cart--open', 'mini-cart--closing']);
      }

      if (host.matches('facet-drawer')) {
        return bodyHasAny(['facet-drawer--opening', 'facet-drawer--open', 'facet-drawer--closing']);
      }

      return false;
    }) || null;
  };

  const getDrawerPanel = (host) => {
    if (!host) return null;
    if (host.matches('header-drawer')) return host.querySelector('.menu-drawer');
    if (host.matches('cart-drawer')) return host.querySelector('.cart-drawer');
    if (host.matches('facet-drawer')) {
      return host.querySelector('.mobile-facets__inner') || host.querySelector('.mobile-facets');
    }
    return null;
  };

  const needsPageLock = () => bodyHasAny(LOCK_CLASSES);

  const lockPage = () => {
    if (locked || !mobile.matches) return;

    lockedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    previousBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow
    };

    document.documentElement.classList.add('bhd-drawer-page-locked');
    document.body.classList.add('bhd-drawer-page-locked');
    document.body.style.top = `-${lockedScrollY}px`;
    locked = true;
  };

  const unlockPage = () => {
    if (!locked) return;

    const restoreY = lockedScrollY;
    document.documentElement.classList.remove('bhd-drawer-page-locked');
    document.body.classList.remove('bhd-drawer-page-locked');

    if (previousBodyStyles) {
      Object.entries(previousBodyStyles).forEach(([property, value]) => {
        document.body.style[property] = value || '';
      });
    }

    locked = false;
    previousBodyStyles = null;
    window.scrollTo(0, restoreY);
  };

  const syncPageLock = () => {
    window.clearTimeout(unlockTimer);

    if (mobile.matches && needsPageLock()) {
      lockPage();
      return;
    }

    unlockTimer = window.setTimeout(() => {
      if (!needsPageLock()) unlockPage();
    }, 40);
  };

  const closeDrawer = (host, event) => {
    if (!host) return;

    const summary = host.querySelector(':scope > details > summary, details > summary');

    if (typeof host.closeMenuDrawer === 'function') {
      host.closeMenuDrawer(event, summary || false);
      return;
    }

    if (summary) {
      summary.click();
      return;
    }

    host.querySelector('details[open]')?.removeAttribute('open');
  };

  const preventBackgroundGesture = (event) => {
    if (!mobile.matches) return;

    const host = getOpenDrawer();
    if (!host) return;

    const panel = getDrawerPanel(host);
    if (!panel || !panel.contains(event.target)) {
      event.preventDefault();
    }
  };

  document.addEventListener('touchmove', preventBackgroundGesture, { passive: false, capture: true });
  document.addEventListener('wheel', preventBackgroundGesture, { passive: false, capture: true });

  document.addEventListener(
    'click',
    (event) => {
      if (!mobile.matches) return;

      const host = getOpenDrawer();
      if (!host) return;

      const panel = getDrawerPanel(host);
      const panelRect = panel?.getBoundingClientRect();
      const clickedOutsidePanel = !panel ||
        !panel.contains(event.target) ||
        (panelRect && (event.clientX < panelRect.left || event.clientX > panelRect.right));

      if (!clickedOutsidePanel) return;

      event.preventDefault();
      event.stopPropagation();
      closeDrawer(host, event);
    },
    true
  );

  const observer = new MutationObserver(syncPageLock);

  const start = () => {
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'open']
    });
    syncPageLock();
  };

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });

  if (mobile.addEventListener) mobile.addEventListener('change', syncPageLock);
  else if (mobile.addListener) mobile.addListener(syncPageLock);

  document.addEventListener('menudrawer:close', () => window.setTimeout(syncPageLock, 450));
  document.addEventListener('shopify:section:load', syncPageLock);
})();